import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
// v0.18.98 (辉哥 10/25 新口径) — 「scheduler 扩展中所有下拉框 (除搜索下拉框) 都用 flarum 原生的 dropdown」。
//   ⇒ 「版本」下拉从 `<select>` 换成 Flarum 原生 `Dropdown` (⛔ 不是手搓的下拉)。
//   ⛔ 用**基础** `Dropdown` (⛔ 不是 `SelectDropdown`): 本扩展既有 33 处全是基础 Dropdown,
//      `SelectDropdown` 的 caret 是 `fas fa-sort` (排序图标) ⇒ 同页两种长相 = 违反「全站一致」。
import Dropdown from 'flarum/common/components/Dropdown';
import Sortable from 'sortablejs';
import Api from '../utils/Api';
// v0.19.22 — 后端错误文案的**共用入口** (三级兜底, 与本文件历史手抄版逐字等价)
import { errReason } from '../utils/FormPrimitives';

/**
 * SchoolScheduleModal — 校区课表配置 (v0.17.58 写侧)
 *
 * 骨架/风格参照 TimeScheduleSection.js + NewVersionModal.js (v0.17.58 两者已删),
 * 拖拽排序范式照抄 SchoolsTab.js 的 rebuildSortables() / onReorderEnd()。
 *
 * ## 与后端的口径必须逐字对齐 (ValidatesScheduleVersionPayload)
 *   后端校验顺序是有讲究的, 前端照抄同一条链、同一种说法:
 *     ① 每个节点自己:  end > start                → 「结束时间必须晚于开始时间」
 *     ② start_time 必须严格递增 (后一个 > 前一个)  → 「完全重复」/「不晚于上一个时间段」
 *   ⚠️ v0.17.62 起**只剩这两条**: 时间段的时间**重叠是允许的**
 *     (10:15-11:00 接 10:30-11:15 合法, 后端与前端都不再拦)。
 *     后端原来的第三条 assertNodesNotOverlapping 与前端原来的第 ④ 步**双双删除** ——
 *     留着前端那道就是「后端放行、前端标红」的半吊子状态, 用户改到合法配置仍被拦下。
 *   ⚠️ 「重复」也不再单列: 同一 start 必然违反严格递增, 判据只留一条;
 *     但文案按「正好相等」/「倒退」分两种说法, 因为用户要解决的问题不同。
 *   ⚠️ **刻意不自动排序**: 顺序是业务语义 (fc 左列从上到下就照 sort_order 渲染),
 *     拖乱了标红让用户自己拖回去, 不偷偷帮他排 (与后端 assertNodesOrdered 的注释同一条决定)。
 *   ⚠️ v0.17.70 新增 ① 里的第三条「5 分钟对齐」是**纯前端规则, 后端没有** ——
 *     后端 validateNodes 只归一化成 HH:MM:SS 再查 end>start 与严格递增。
 *     它存在的理由是移动端换成原生 input[type=time] 后粒度不再由控件保证 (见 validate 内注释)。
 *
 * ## 时间选择器 — **移动端原生 / 桌面 flatpickr 双轨** (v0.17.70)
 *   两套并存, 各由一行 JSX 决定, 别试图合并:
 *
 *   - **桌面端**: `type="text"` + `data-fp` + flatpickr 纯时间模式
 *     (enableTime + noCalendar + time_24hr + minuteIncrement:5),
 *     vendor 在 ziiven-ziven-core/flatpickr/, 本扩展 index.js 已加载, 不新增依赖。
 *     ⚠️ 改值必须 `el._flatpickr.setDate(...)` —— 直接调全局 `flatpickr(el)` 会另起一个实例,
 *     组件侧的 onChange 不会被触发 (行内报错不会跟着重算)。
 *     ⚠️ `disableMobile: true` 仍是**必需项**: 漏掉它 = iOS 真机上每行出现两个一模一样的
 *     时间框, 其中一个是死的 (flatpickr 的 isMobile 分支会克隆原 input, 见 syncPickers 内注释)。
 *     **别用桌面浏览器验证这个 bug**: isMobile 是 UA 正则判的, 桌面引擎 100% 命中不了。
 *
 *   - **移动端**: 直接渲染原生 `<input type="time" step="300">`, **不挂 flatpickr**。
 *     辉哥 10/16 报「弹出的时间选择器太难看了」—— v0.17.69 修掉「每行四个框」时,
 *     `disableMobile: true` 把 iOS 的**系统原生转盘**换成了 flatpickr 的**桌面数字弹层**
 *     (一条扁横条、盖住下面两行)。框数对了但观感是倒退的, 所以这条路走不通, 只能换控件。
 *     ⚠️ 移动端这两个 input **故意不带 `data-fp`** —— syncPickers() 靠
 *     `querySelector('[data-fp="…"]')` 取元素, 没有这个属性就自然整个跳过, 桌面那套一行都不用改。
 *     ⚠️ `<input type="time">` 的 `value` 形如 `"10:15"`, 与 flatpickr `dateFormat:'H:i'`
 *     的输出**逐字一致**, 所以 node[start_time/end_time] 与保存 payload 都不用改。
 */

// 'HH:MM' → 当天第几分钟; 非法返 null
function toMinutes(hhmm) {
  if (typeof hhmm !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = +m[1];
  const i = +m[2];
  if (h > 23 || i > 59) return null;
  return h * 60 + i;
}

/**
 * v0.17.70 — 'HH:MM' → 原生 `<input type="time">` 能吃的 'HH:MM'; 非法一律回 ''。
 *
 * ⚠️ 为什么不能把 node[field] 原样丢给 value=:
 *   原生 time 控件收到非法值是**静默显示空白**, 不是报错 —— 后端哪天多给一位秒
 *   ('HH:MM:SS') 或少一位补零 ('H:MM'), 界面就变成一片空白且无从排查。
 *   所以这里统一过一遍 toMinutes 再格式化, 把非法值显式收敛成 ''。
 */
function toInputTime(v) {
  // slice(0,5) 兜住 'HH:MM:SS': 后端 ScheduleNode::formatTime 本来就只给 'HH:MM',
  // 但这是**显示层**的输入闸门, 不该因为上游哪天改了格式就整片变空白。
  const m = toMinutes(String(v == null ? '' : v).trim().slice(0, 5));
  if (m === null) return '';
  return String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');
}

/** 分钟 → 'Xh Ym' / 'Ym', 给「间隔」「时长」标签用 */
function fmtDuration(min) {
  if (min == null || min < 0) return '—';
  const h = Math.floor(min / 60);
  const i = min % 60;
  if (h && i) return h + 'h' + i + 'm';
  if (h) return h + 'h';
  return i + 'm';
}
let seq = 0;
/** 行 key: 只活在一次编辑会话里, 用来给 flatpickr 实例 + 校验结果定位, 不是数据库 id */
function nextKey() {
  return 'n' + ++seq + '_' + Math.random().toString(36).slice(2, 7);
}
export default class SchoolScheduleModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.school = vnode.attrs.school || null;
    this.schoolId = this.school && this.school.id;
    this.onSaved = vnode.attrs.onSaved || null;
    this.loading = true;
    this.saving = false;
    this.error = '';
    this.versions = [];
    this.listWarning = '';

    // editingVid = 正在编辑的版本 effective_from; null = 新建 (POST)
    this.editingVid = null;
    this.form = this.blankForm();

    // flatpickr 实例按 `nodeKey:field` 存, 便于 remove/重排后精确 destroy, 避免泄漏
    this.pickers = new Map();
    this.sortable = null;

    // v0.17.67 — 删除二次确认的待删对象 (null = 没有待删)。为 null 时确认框不渲染任何 DOM,
    //   所以桌面常态 DOM 与改动前逐元素一致 (判据 4 零回归)。
    this.pendingDel = null;

    // v0.17.67 — 移动端紧凑行。断点 768 与 forum.less 主流写法 (@media (max-width: 768px))
    //   同值, 也跟 StatsTab.isMobile 同款, 避免 LESS 与 JSX 两处断点各写各的。
    this.isMobileLayout = this.computeMobileLayout();
    this.canManage = this.resolveCanManage();
    this.load();
    this._onResize = () => {
      const next = this.computeMobileLayout();
      if (next !== this.isMobileLayout) {
        this.isMobileLayout = next;
        m.redraw();
      }
    };
    window.addEventListener('resize', this._onResize);
  }
  onremove(vnode) {
    super.onremove(vnode);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    this.destroyPickers();
    if (this.sortable) {
      try {
        this.sortable.destroy();
      } catch (e) {}
      this.sortable = null;
    }
  }

  /**
   * v0.17.67 — 移动端紧凑行开关。
   *
   * ⚠️ 为什么 JS 也要判一遍而不只靠 CSS 媒体查询: 移动端行的 DOM 与桌面**不同**
   *   (时长下沉到连接线、行内不挂拖拽手柄), 只靠 CSS 隐藏是切不出另一套结构的。
   *   LESS 侧对应的那一整块包在 `@media (max-width: 768px)` 里, 两边同断点。
   */
  computeMobileLayout() {
    return typeof window !== 'undefined' && window.innerWidth <= 768;
  }

  /**
   * 沿用 TimeScheduleSection 的判法 (Flarum 2.0 没有 hasPermission):
   * admin 或 permissions.scheduler.manageLessons 才给写操作。
   * 后端 5 个写路由要的也正是 scheduler.manageLessons (各 Controller L54 一致)。
   */
  resolveCanManage() {
    const u = app.session && app.session.user;
    if (!u) return false;
    try {
      if (u.isAdmin && u.isAdmin()) return true;
      const perms = u.attribute && u.attribute('permissions') || {};
      return !!perms['scheduler.manageLessons'];
    } catch (e) {
      return false;
    }
  }
  todayISO() {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }
  blankForm() {
    return {
      version_name: '',
      // v0.17.84 (辉哥拍板方案甲) —— 搬运工的入口之一: 新建时开始日期就是**今天**。
      //   「生效起始日期」输入框已从 modal 删掉, 用户没有任何入口改它 ⇒ 这里就是唯一的取值来源。
      //   ⚠️ 不要因为「UI 上没有这个字段」就把它删掉 —— submit() 原样送出, 后端 POST 必读。
      effective_from: this.todayISO(),
      effective_to: '',
      notes: '',
      nodes: []
    };
  }

  // ── 数据加载 ────────────────────────────────────────────────

  async load() {
    this.loading = true;
    m.redraw();
    try {
      const res = await Api.listScheduleVersions(this.schoolId);
      this.versions = res.versions;
      // 同一校区多条版本同时生效属数据异常, 后端在列表响应里显式给了 warning,
      // 原样透出 (别自己编一套说法, 也不要吞掉 —— 它正是「fc 会 500」的根因提示)。
      this.listWarning = res.warning || '';
    } catch (e) {
      console.error('[school-schedule] list versions failed', e);
      this.versions = [];
      this.error = this.extractError(e);
    } finally {
      this.loading = false;
      // 默认打开「当前生效版」; 没有当前版就打开最近一版; 一个版本都没有 = 空态 (new 模式)
      const current = this.currentVersion();
      if (current) this.selectVersion(current);else if (this.versions.length) this.selectVersion(this.versions[0]);
      // 一个版本都没有 = 该校区还没配过课表 ⇒ 停在**空态** (而不是给一行默认数据),
      // 让「还没配置, 去配置」这句话真的出现, 用户点「添加时间段」才开始配。
      else {
        this.editingVid = null;
        this.form = this.blankForm();
        this.error = '';
      }
      m.redraw();
      this.rebuildSortable();
    }
  }
  currentVersion() {
    return this.versions.find(v => v.is_current) || null;
  }
  versionLabel(v) {
    const name = v.version_name || '(未命名)';
    // ⚠️ v0.17.85 推翻 v0.17.83「显示层恒显至今」—— 那条取舍的前提已经不成立了。
    //   v0.17.83 的前提是「effective_to 是可有可无的摆设」, 所以干脆不显示它;
    //   但从 v0.17.85 起, **新建版本时旧版本的 effective_to 由后端自动写入**
    //   (CreateScheduleVersionController 把被接管的那一版封到新版.effective_from),
    //   它变成了真数据。一律显「至今」就是骗人: 明明 10-17 就结束了, 却告诉运营还生效着。
    //   辉哥 10/17 拍板: 显示真实结束日。
    //   ⚠️ 区间是**左闭右开** (ScheduleResolver::coversDate): effective_to 那天**归下一版**,
    //     所以显示成「2026-10-01 → 2026-10-17」意思是「10-16 及以前用它」, 不是「10-17 那天还用它」。
    //     NULL 才是真的「至今」。
    return name + ' · ' + v.effective_from + ' → ' + (v.effective_to || '至今');
  }

  /** 把某个版本装进表单 (编辑态) */
  selectVersion(v) {
    this.destroyPickers();
    this.editingVid = v.effective_from;
    this.form = {
      version_name: v.version_name || '',
      // v0.17.84 (辉哥拍板方案甲) —— 搬运工的入口之三, 也是三条路径里**唯一**装服务端原值的:
      //   编辑一个存量版本时, 它的开始日期可能是几个月前 (甚至更早) 存的, 必须**原值搬运**,
      //   绝不能改成 this.todayISO() —— 那会把所有存量版本的开始日期悄悄改写成今天。
      // ⚠️ 这一行同时是**版本身份本身**: editingVid、PUT URL 里的 vid、版本下拉的 value
      //   全都取自它。删掉 ⇒ editingVid 变 undefined ⇒ PUT 的 URL 缺 vid ⇒ 编辑流程直接崩。
      effective_from: v.effective_from,
      // v0.17.82 (辉哥拍板方案甲) —— effective_to 现在是**不可见的搬运工**, 不是表单字段:
      //   UI 已删, 但这里仍把原值装进 form, 由 submit 原样带回后端, 好让「本来就有结束日期的
      //   版本」编辑后**保持原值**。若把这一行改成 '' (或删掉), 就退化成方案乙, 后端
      //   UpdateScheduleVersionController:70 会把已有的日期静默抹成 NULL。**别动。**
      effective_to: v.effective_to || '',
      notes: v.notes || '',
      nodes: (v.nodes || []).map(n => ({
        key: nextKey(),
        start_time: n.start_time,
        end_time: n.end_time
      }))
    };
    this.error = '';
  }

  /** [+ 新建版本]: 空表单, 日期默认今天 */
  startNew() {
    this.destroyPickers();
    this.editingVid = null;
    this.form = this.blankForm();
    this.form.nodes = [{
      key: nextKey(),
      start_time: '09:00',
      end_time: '12:00'
    }];
    this.error = '';
    m.redraw();
    this.rebuildSortable();
    this.syncPickers();
  }

  /** [复制为新版本]: 带上一版的节点, 但走新建 (effective_from 固定为今天 —— v0.17.84 起输入框已删, 用户不可改) */
  copyAsNew(v) {
    this.destroyPickers();
    this.editingVid = null;
    this.form = {
      version_name: (v.version_name || '') + ' 副本',
      // v0.17.84 (辉哥拍板方案甲) —— 搬运工的入口之二: 复制虽然带着上一版的一切内容, 但
      //   走的是 POST 新建 ⇒ 开始日期同样取今天 (而不是沿用来源版本的老日期)。
      effective_from: this.todayISO(),
      effective_to: '',
      notes: v.notes || '',
      nodes: (v.nodes || []).map(n => ({
        key: nextKey(),
        start_time: n.start_time,
        end_time: n.end_time
      }))
    };
    if (!this.form.nodes.length) {
      this.form.nodes = [{
        key: nextKey(),
        start_time: '09:00',
        end_time: '12:00'
      }];
    }
    this.error = '';
    m.redraw();
    this.rebuildSortable();
    this.syncPickers();
  }

  // ── 校验 (逐条对齐后端 ValidatesScheduleVersionPayload) ─────────

  /**
   * @returns {{ok:boolean, errors:Object<string,string>}} errors: nodeKey → 行内提示
   */
  validate() {
    const errors = {};
    const nodes = this.form.nodes;
    const blocked = why => ({
      ok: false,
      errors,
      blocked: why
    });
    if (!this.form.version_name.trim()) {
      return {
        ok: false,
        errors: {},
        global: '版本名必填'
      };
    }
    // v0.17.84 (辉哥拍板) —— 「生效起始日期」输入框已删, 但**这条校验保留**: 它现在只是一条
    //   **只读守卫**, 不是用户可触发的交互校验 (UI 上已经没有任何途径把它写成非法值)。
    //   留着是因为它很便宜, 且能挡住两类真实故障: (a) todayISO() 哪天算错/时区跨天,
    //   (b) selectVersion() 装进来的服务端历史脏数据不是 YYYY-MM-DD。
    //   这两种情况下挡在提交前, 好过把一个 undefined/空串原样发给后端换一个更费解的报错。
    if (!/^\d{4}-\d{2}-\d{2}$/.test(this.form.effective_from)) {
      return {
        ok: false,
        errors: {},
        global: '生效起始日期必填 (YYYY-MM-DD)'
      };
    }
    // v0.17.82 (辉哥拍板) —— 「生效结束日期」输入框已从 modal 里删掉, 这里两条校验一并删掉:
    //   没有输入框 ⇒ 用户无法触发 ⇒ 留着只会让「历史数据里 to <= from 的老版本」在编辑时
    //   撞上一条用户根本看不见、改不了的报错。v0.17.83 在下面补了一条**只读**兜底
    //   (提前拦下并提示联系管理员), 后端 ValidatesScheduleVersionPayload 仍照旧兜底。
    //
    // v0.17.83 (辉哥拍板) —— 兜底「历史脏版本」, 插在 effective_from 格式校验之后、nodes 校验之前:
    //   输入框没了, 但库里可能存在 effective_to <= effective_from 的老版本。用户编辑它保存时
    //   若前端不拦, 会撞后端 422 (ValidatesScheduleVersionPayload「生效结束日期必须晚于起始日期」)
    //   —— 而用户已经没有输入框可改, 成了死路。这里提前拦下, 并把「这不是他能修的」说清楚。
    //   ⚠️ **只读检查**: 只看搬运工带进来的原值, 不写回、不清空 (方案甲要求原值原样带回)。
    //   ⚠️ 条件写成「非空 且 并不是晚于 effective_from」而不是直接比大小, 但两者**等价**:
    //     effective_to 只可能是 null 或 'YYYY-MM-DD' —— 建表时是 `$table->date('effective_to')
    //     ->nullable()` (migrations/2026_10_16_000034_create_schedule_versions_and_nodes.php),
    //     MySQL 的 DATE 列存不了非法串, 接口侧 ScheduleResolver::dateOf() 又统一格式化成 Y-m-d
    //     ⇒ 对这两个取值, `!(to > from)` 就是 `to <= from`, 真实存在的那类脏数据照拦不误。
    //   ⚠️ 但它**不是** fail-closed: JS 的 > 走字典序而非日期语义, 格式非法的值会漏过去
    //     ('2026/10/17' / '99999-99-99' / 'abc' / '2026-13-45' 实测都放行 —— 'abc' > '2026-…'
    //     为真, 因为 'a'=0x61 > '2'=0x32, 于是 !(to > from) 为 false)。本库不可达 (见上),
    //     所以代码不必动; 但别再当「格式不对的老数据这里也能拦」来依赖它。
    //   ⚠️ 给未来的维护者: 若哪天把该列改成 VARCHAR / 允许自由文本, 这条兜底就不再 fail-closed,
    //     届时必须补格式校验 (比照上面 effective_from 的 /^\d{4}-\d{2}-\d{2}$/), 否则格式非法
    //     的老数据会漏出去撞后端 422 —— 而用户已经没有输入框可改。
    //   新建 (blankForm) / 复制为新版本 (copyAsNew) 的 effective_to 都是 '' ⇒ 本条自然不触发。
    const carriedTo = String(this.form.effective_to || '');
    if (carriedTo && !(carriedTo > this.form.effective_from)) {
      return {
        ok: false,
        errors: {},
        global: '这个课表版本的历史数据有异常 (生效结束日期不晚于生效起始日期), ' + '你在页面上无法修改它, 请联系管理员处理',
        // ⚠️ render() 自己也会跑一次 validate() 来决定「保存」按钮置不置灰, 所以这条拦截
        //   在**用户点保存之前**就已经生效了 ⇒ submit() 压根不会跑, 它设的 this.error
        //   永远不会出现 (只剩一个灰按钮, 也就是本轮要消灭的那种死路)。
        //   用 dataFault 标一下, 让 render 把这句话直接显示出来。
        dataFault: true
      };
    }
    if (nodes.length === 0) {
      return {
        ok: false,
        errors: {},
        global: '至少需要 1 个时间段 (nodes)'
      };
    }
    if (nodes.length > 48) {
      return {
        ok: false,
        errors: {},
        global: '时间段最多 48 个'
      };
    }
    const hm = (n, f) => String(nodes[n][f] || '').slice(0, 5);

    // ① 每个节点自己合法吗 (后端 validateNodes 循环体)
    for (let i = 0; i < nodes.length; i++) {
      const s = toMinutes(nodes[i].start_time);
      const e = toMinutes(nodes[i].end_time);
      if (s === null) {
        errors[nodes[i].key] = '第 ' + (i + 1) + ' 个时间段的 start_time 必填 (HH:MM)';
        continue;
      }
      if (e === null) {
        errors[nodes[i].key] = '第 ' + (i + 1) + ' 个时间段的 end_time 必填 (HH:MM)';
        continue;
      }
      if (e <= s) {
        errors[nodes[i].key] = '第 ' + (i + 1) + ' 个时间段非法: 结束时间必须晚于开始时间 (' + String(nodes[i].start_time) + ' → ' + String(nodes[i].end_time) + ')';
        continue;
      }
      // v0.17.70 — 5 分钟对齐。**后端没有这条** (validateNodes 只做 toTimeString 归一化 +
      //   end>start + 严格递增, 迁移 000034 也没给这两列加 CHECK), 所以只能在这里兜。
      //   为什么必须有: 移动端换原生 <input type="time"> 后, 5 分钟粒度**不再由控件保证**
      //   —— `step="300"` 只是给转盘的步进提示, iOS 转盘照样能停在 09:47。
      //   桌面端 flatpickr 的 minuteIncrement:5 也只是**选择器**的粒度, allowInput 让人能手打
      //   任意分钟 ⇒ 两端都堵不住, 统一在 validate 里拦。
      //   ⚠️ 刻意**报错而不是四舍五入**: 课表是对外承诺的约课档位, 静默把 09:47 改成 09:45
      //     等于偷偷改用户填的数据 (与「不自动排序」同一条原则)。
      if (s % 5 !== 0 || e % 5 !== 0) {
        const off = [];
        if (s % 5 !== 0) off.push('开始 ' + hm(i, 'start_time'));
        if (e % 5 !== 0) off.push('结束 ' + hm(i, 'end_time'));
        errors[nodes[i].key] = '第 ' + (i + 1) + ' 个时间段的' + off.join(' / ') + ' 不是 5 分钟对齐, 请选整 5 分钟 (如 09:45 / 09:50)';
      }
    }
    if (Object.keys(errors).length) return blocked('illegal');

    // ② start_time 必须严格递增 —— 刻意不自动排序, 标红让用户自己拖
    //    ⚠️ 「完全重复」是这一条的**子集** (同一 start 必然不递增), 不再单列判据,
    //      但文案分两种说法: 正好相等 = 手滑拖重了, 倒退 = 顺序排错了。
    //    ⚠️ v0.17.62: 时间区间**重叠不再校验** (原第 ④ 步已删) —— 前端放行, 后端也放行。
    for (let i = 1; i < nodes.length; i++) {
      if (nodes[i].start_time > nodes[i - 1].start_time) continue;
      if (nodes[i].start_time === nodes[i - 1].start_time) {
        errors[nodes[i].key] = '第 ' + (i + 1) + ' 个时间段的开始时间 ' + hm(i, 'start_time') + ' 与第 ' + i + ' 个时间段完全重复: 同一开始时间只能有一行, 重复的那行永远收不到课';
      } else {
        errors[nodes[i].key] = '第 ' + (i + 1) + ' 个时间段 (' + hm(i, 'start_time') + '-' + hm(i, 'end_time') + ') 的开始时间 ' + hm(i, 'start_time') + ' 不晚于上一个时间段 (' + hm(i - 1, 'start_time') + '-' + hm(i - 1, 'end_time') + '): 时间段必须按开始时间从小到大排列';
      }
    }
    if (Object.keys(errors).length) return blocked('unordered');
    return {
      ok: true,
      errors: {}
    };
  }

  /**
   * 行红框按当前表单实时算 (content() 每次 redraw 都重跑一遍 validate 并刷新 _rowErrors),
   * 所以没有一处「先标红后忘了清」的缓存坑。
   */
  rowError(key) {
    if (!this._rowErrors) return null;
    return this._rowErrors[key] || null;
  }

  // ── 行的增删 / 拖拽 ──────────────────────────────────────────

  addNode() {
    this.form.nodes.push({
      key: nextKey(),
      start_time: '09:00',
      end_time: '12:00'
    });
    this.error = '';
    this.pendingDel = null;
    m.redraw();
    this.rebuildSortable();
    this.syncPickers();
  }

  /**
   * v0.17.67 — 删除二次确认 (跨端)。
   *
   * 改前点一下垃圾桶就直接少掉一整段可约时间, 不可逆, 手上一次误触就没了。
   * 现在先弹确认, 文案带段号 + 起止时间, 让操作者在删之前确认自己删的是哪一段。
   *
   * 确认框的形状照抄本仓 StudentContractModal.confirmView() —— Flarum 2 没有自带
   * ConfirmationDialog (它自己在 L618 注释里交代过这点), 所以确认框画在本 Modal 内部,
   * 不叠第二层弹窗。本组件沿用同一条路子。
   */
  askRemoveNode(node, i) {
    this.pendingDel = {
      key: node.key,
      no: i + 1,
      start: node.start_time || '',
      end: node.end_time || ''
    };
    m.redraw();
  }

  /**
   * v0.17.71 — 确认框兜底: 保证完整落在视口内。
   *
   * CSS 那侧已经把遮罩盒收成一个视口高 (`.ZS-sched-veil { height:100dvh }`), 所以在
   * **弹窗没被滚动过**的常态里, 确认框天然就落在可视区中间, 这里什么都不做。
   *
   * 还剩一个 CSS 救不了的情况: 用户先在弹窗里往下滚了一段, 再点某一段的删除 ——
   * 此时遮罩盒跟着内容一起上移出视口, 确认框就跑到屏幕外了。真的滚动容器是
   * `.ModalManager`, 而 `.Modal-content` 的 `overflow:hidden` 让 sticky 绑错对象
   * (实测见报告), 所以这里直接把它滚进可视区, 行为跟系统确认框一致。
   *
   * 判据是「完整在视口内」, 所以已经摆正时是一次 getBoundingClientRect 就返回的
   * 空操作, 不会打断用户正在拖拽/编辑的时间段。
   */
  ensureConfirmVisible() {
    // 取法跟本组件 syncPickers() 一致 (this.element.querySelector), 不用 JSX ref ——
    // 本仓这套 Mithril 不把 JSX 的 ref 当 vnode ref 用, 会原样渲染成 DOM 属性。
    const el = this.element && this.element.querySelector('.ZS-sched-cf');
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (!r.height) return; // 还没渲染出来 (或已卸载)
    if (r.top >= 0 && r.bottom <= window.innerHeight) return;
    el.scrollIntoView({
      block: 'center'
    });
  }
  cancelRemoveNode() {
    this.pendingDel = null;
    m.redraw();
  }
  removeNode(key) {
    this.form.nodes = this.form.nodes.filter(n => n.key !== key);
    this.destroyPickers();
    this.error = '';
    this.pendingDel = null;
    m.redraw();
    this.rebuildSortable();
    this.syncPickers();
  }

  // 拖拽结束 → splice 重排, sort_order 就是新下标 (保存时按数组顺序全量提交)
  onReorderEnd(evt) {
    if (evt.oldIndex === evt.newIndex) return;
    if (evt.oldIndex == null || evt.newIndex == null) return;
    const [moved] = this.form.nodes.splice(evt.oldIndex, 1);
    this.form.nodes.splice(evt.newIndex, 0, moved);
    this.error = '';
    m.redraw();
    this.rebuildSortable();
    this.syncPickers();
  }

  // 范式照抄 SchoolsTab.rebuildSortables(): 元素可能在挂载后才在 DOM 里, 轮询几次
  rebuildSortable() {
    if (this.sortable) {
      try {
        this.sortable.destroy();
      } catch (e) {}
      this.sortable = null;
    }
    // ⚠️ v0.17.67: 移动端不挂 Sortable。移动端 `.ZS-sched-nodes` 的子节点是
    //   [行, 连接线, 行, 连接线, ...], 连接线也会被 Sortable 当成一个可拖项,
    //   于是 oldIndex/newIndex 与 form.nodes 的下标对不上 (onReorderEnd 按下标 splice),
    //   拖一次就把课表排乱。移动端行内本来也没有拖拽手柄, 直接不初始化最干净。
    if (this.isMobileLayout) return;
    const el = this.element && this.element.querySelector('.ZS-sched-nodes');
    if (!el) return;
    this.sortable = Sortable.create(el, {
      animation: 150,
      handle: '.ZS-sched-handle',
      ghostClass: 'ZS-sortable-ghost',
      dragClass: 'ZS-sortable-drag',
      onEnd: evt => this.onReorderEnd(evt)
    });
  }

  // ── 移动端原生时间控件 (v0.17.70) ────────────────────────────

  /**
   * 原生 `<input type="time">` 的取值入口 —— 移动端那两个框的唯一写回点。
   *
   * 照抄 flatpickr onChange 的三步 (写回 model → 清 _rowErrors → redraw), 语义一致:
   *   ① 必须写回 node[field]: 移动端走的是 mithril **受控 value 绑定**, 不写回的话
   *      下一次 redraw 会把用户刚选的值刷回旧值 (mithril 的 _values 缓存只认 vnode 值)。
   *   ② 必须清 _rowErrors 再 redraw: content() 每次都会重跑 validate(), 但缓存不清的话
   *      本次渲染还会先用旧缓存渲染一遍, 用户会看到「刚改完还是红的」闪一下。
   *   ③ `el.value` 天然就是 'HH:MM', 与桌面 flatpickr dateFormat:'H:i' 同格式, 不用再转。
   */
  onNativeTimeChange(node, field, el) {
    const v = el.value || '';
    // 原生控件只在值真的变了才发 change, 这个等号分支基本只在「清空后又填回原值」时命中;
    // 留着是为了不在空转时白白 redraw 一次。
    if ((node[field] || '') === v) return;
    node[field] = v;
    this._rowErrors = null;
    m.redraw();
  }

  // ── flatpickr 纯时间选择器 (仅桌面) ──────────────────────────

  /**
   * 把每一行的两个 input 挂上/刷新 flatpickr。
   *
   * ⚠️ 已存在的实例走 `el._flatpickr.setDate(v, false)` —— 直接再调 `window.flatpickr(el)`
   *   会另起一个实例, 组件 onChange 不再触发, 行内报错不会重算 (这也是本组件不用
   *   `value=` 受控的原因: flatpickr 自己管 input 的显示值)。
   */
  syncPickers() {
    if (typeof window === 'undefined' || typeof window.flatpickr !== 'function') return;

    // v0.17.70 — 移动端早退: 那两个框是原生 input[type=time], **没有 data-fp**,
    //   下面的 querySelector 本来就找不到它们, 一个实例都不会建。
    //   这里先把可能残留的**桌面**实例清掉再返回, 是为了 isMobileLayout 这个边界情况:
    //   它按 window.innerWidth <= 768 判, 不按触屏判 ⇒ 桌面浏览器把窗口拖窄时
    //   DOM 会从 [data-fp] 换成原生 input, 而下面那段按 form.nodes 拼 id 的清理循环
    //   仍然认为这些 id "还活着" (它只看 model 不看 DOM), 于是那批实例会一直挂在
    //   已脱离文档的节点上不销毁。
    if (this.isMobileLayout) {
      this.destroyPickers();
      return;
    }
    const alive = new Set();
    this.form.nodes.forEach(node => {
      ['start_time', 'end_time'].forEach(field => {
        const id = node.key + ':' + field;
        alive.add(id);
        const el = this.element && this.element.querySelector('[data-fp="' + id + '"]');
        if (!el) return;
        const value = node[field] || '';
        if (el._flatpickr) {
          // 反手把外部改动 (拖拽不涉及时间, 但复制版本会) 同步回输入框
          const cur = el._flatpickr.formatDate(el._flatpickr.selectedDates[0], 'H:i');
          if (cur !== value) el._flatpickr.setDate(value, false);
          return;
        }
        try {
          const inst = window.flatpickr(el, {
            enableTime: true,
            noCalendar: true,
            time_24hr: true,
            dateFormat: 'H:i',
            minuteIncrement: 5,
            allowInput: true,
            // SOP 106 — popover 脱离 modal 的 stacking context
            appendTo: document.body,
            // v0.17.69 — iOS 上「每行两个时间框」的真凶 (辉哥 10/16 报: 开始/结束各两个框,
            //   只有一个能弹)。
            //
            //   ⚠️ v0.17.70 之后: **窄屏 (≤768) 的移动端已经不走 flatpickr 了** (原生 input),
            //     这条选项改由「移动 UA + 宽度 > 768」的场景兜底 —— 典型就是 **iPad 横屏**
            //     (UA 命中 isMobile 正则, 但 innerWidth 1024 ⇒ isMobileLayout=false ⇒ 走桌面分支
            //     挂 flatpickr ⇒ 不加这条照样会克隆出一对死框)。**别因为「移动端不挂了」就删它。**
            //
            //   根因是 flatpickr vendor 的 **isMobile 原生输入分支**, 不是 mithril 重建:
            //     isMobile 为真时, flatpickr 会**自己再克隆一个 input**
            //     (`w.mobileInput = d("input", w.input.className + " flatpickr-mobile")`),
            //     并 `insertBefore` 到原 input 后面 (vendor flatpickr.js L74282 附近)。
            //     关键: clone **复制了原 input 的 className** —— 所以它同样带 `.ZS-sched-time`,
            //     于是**界面上并排两个一模一样的框**。实测 (WebKit + iPhone UA, 21 段):
            //     `input.ZS-sched-time` = **84** 个, 而 `[data-fp]` = 42 个 (21×2) —— 正好两倍。
            //
            //   为什么「只有一个能弹」: clone 没有 `data-fp`, 而 syncPickers() 用
            //     `querySelector('[data-fp="…"]')` 取元素 —— querySelector 只认第一个匹配,
            //     clone **永远拿不到 `_flatpickr`** ⇒ 它就是那个死的框。
            //     实测: 原框 hasFp=true / clone hasFp=false, 且两者 value 相同 (10:15 与 10:15:00)。
            //
            //   ⚠️ `w.isMobile` 的判定 (vendor L63407) 是 UA 正则
            //     /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
            //     ⇒ **只有真机 iOS/安卓浏览器会命中**, 桌面 Chromium/WebKit 用默认 UA
            //     100% 命中不了 —— 所以这个 bug 在任何桌面浏览器里都是「复现不出来」的假绿,
            //     验证必须 `user_agent` 设成 iPhone (或真机), 别信桌面结果。
            //
            //   修法用 flatpickr **原生选项** disableMobile (不 monkey patch、不改 vendor、
            //   不用 innerHTML 硬刷), 与本仓 DatePickerHelper.js / CalendarTab.js / SalesTab.js
            //   的既有做法一致 (SOP 105/106 就是围绕 disableMobile + appendTo:body 定的)。
            //   ⚠️ v0.17.70 曾记在这里的代价「iOS 上从此走 flatpickr popover 而不是系统原生转盘」
            //     **已作废**: 窄屏移动端不再挂 flatpickr, 直接渲染原生 input[type=time] (见 nodeRow)。
            //     flatpickr 现在只服务桌面 (含 iPad 横屏那种「宽屏移动 UA」)。
            disableMobile: true,
            // ziven-core 的 flatpickr.min.css 是浅色皮肤, 落到本扩展深色 modal 里会白底黑字。
            // 打一个作用域 class 让 LESS 只覆盖本组件的面板, 不污染别的扩展 (同 DatePickerHelper 的 .ZS-fp 做法)。
            onReady: (selectedDates, dateStr, fp) => {
              if (fp && fp.calendarContainer) fp.calendarContainer.classList.add('ZS-fp-time');
            },
            onChange: (selectedDates, dateStr) => {
              node[field] = dateStr;
              this._rowErrors = null;
              m.redraw();
            }
          });
          this.pickers.set(id, inst);
          if (value) inst.setDate(value, false);
        } catch (e) {
          console.error('[school-schedule] flatpickr init failed', e);
        }
      });
    });

    // 清理已经不存在的行 (删行 / 重排后 DOM 换掉) 的实例
    for (const [id, inst] of Array.from(this.pickers.entries())) {
      if (alive.has(id)) continue;
      try {
        inst.destroy();
      } catch (e) {}
      this.pickers.delete(id);
    }
  }
  destroyPickers() {
    for (const inst of this.pickers.values()) {
      try {
        inst.destroy();
      } catch (e) {}
    }
    this.pickers.clear();
  }

  // ── 保存 / 删除版本 ─────────────────────────────────────────

  extractError(e) {
    // v0.17.66 — 这处改前写的是 `(e && e.message) || (...detail) || '操作失败'`。
    //   ⚠️ **它并不是「顺序反了导致 detail 被挡掉」的稳错** —— 那个说法是错的，见下。
    //   硬事实 (Mavis 在 public/assets/forum.js 的 minified 类体里复核过):
    //   Flarum 抛到 catch 的错误类是 core/common/utils/RequestError, 它是**普通 class、
    //   不继承 Error**, 所以压根没有 message 属性 ——
    //     class n{constructor(t,e,r,n){this.status=t;this.responseText=e;this.options=r;
    //       this.xhr=n;try{this.response=JSON.parse(e??"null")}catch(t){this.response=null}
    //       this.alert=null}}
    //   自有键只有 status/responseText/options/xhr/response/alert 六个。
    //   ⇒ **e.message 在本构建上恒为 undefined, 全仓所有 `(e && e.message)` 都是死代码。**
    //   ⇒ 改前的顺序之所以「能用」, 唯一理由就是 undefined 恰好 falsy, 恰好落到 detail。
    //
    //   那为什么还要改? 因为**在错误的默认值上押注比稳错更危险**:
    //   ① 不再依赖「e.message 恰好为空」这个隐式前提 —— 那是它正常工作的唯一理由;
    //   ② 一旦 Flarum 给 RequestError 加上 message (或换 XHR 层、直接抛真 Error),
    //      旧顺序会从「能用」**突变成「吞掉」**, 而且没有测试会红。
    //   兜底文案「操作失败」原文保留, 降为最后一档 (网络层异常 / 非 422 时仍命中)。
    return errReason(e, '操作失败');
  }
  async submit() {
    const v = this.validate();
    this._rowErrors = v.errors;
    this._globalError = v.global || null;
    if (!v.ok) {
      this.error = v.global || '有时间段未通过校验, 请按标红的行修正后再保存';
      m.redraw();
      return;
    }
    this.error = '';
    this.saving = true;
    m.redraw();
    const payload = {
      version_name: this.form.version_name.trim(),
      // v0.17.84 (辉哥拍板方案甲) —— 搬运工的出口 (与 v0.17.82 的 effective_to 并列):
      //   effective_from **必须留在 payload 里**。两条路径:
      //     POST 新建 —— 后端直接读这个字段落库, 少了 ⇒ 建不出带开始日期的版本;
      //     PUT 编辑 —— 后端 UpdateScheduleVersionController 以 **URL 的 vid** 定位版本,
      //                  正文里的这个字段在 update 路径上**根本不读**。但它**不能因为「不读」
      //                  就删掉**: 它是同一套搬运工机制的一半, 留着才能让「保存前后的 payload
      //                  语义」与 UI 的版本身份保持一致, 也好在网络面板里自查。
      //   **这一行一个字都别改**, 更不要写进「死代码清理」里。
      effective_from: this.form.effective_from,
      // v0.17.82 (辉哥拍板方案甲) —— 搬运工的出口: 原值原样送出, 语义与改动前**逐字一致**。
      //   这里绝不能改成「不发这个字段」或「恒发 null」—— 后端是**无条件覆盖写**
      //   (UpdateScheduleVersionController:70), 少发/发 null ⇒ 已有结束日期被抹掉 (方案乙)。
      //   新建/复制走的是 '' ⇒ 送出 null, 这正是期望的「无限期」。
      effective_to: this.form.effective_to || null,
      notes: this.form.notes ? this.form.notes : null,
      nodes: this.form.nodes.map(n => ({
        start_time: n.start_time,
        end_time: n.end_time
      }))
    };
    try {
      if (this.editingVid) await Api.updateScheduleVersion(this.schoolId, this.editingVid, payload);else await Api.createScheduleVersion(this.schoolId, payload);
      this.saving = false;
      this.destroyPickers();
      // 重载一次拿服务端规范化后的结果 (时间统一补成 HH:MM, 且顺带刷出新版本列表)
      await this.load();
      if (this.onSaved) this.onSaved();
      this.hide();
    } catch (e) {
      this.saving = false;
      this.error = this.extractError(e);
      console.error('[school-schedule] save failed', e);
      m.redraw();
    }
  }
  async deleteVersion(v) {
    if (v.is_current) return;
    const name = v.version_name || v.effective_from;
    if (!confirm('确认删除课表版本 "' + name + '"?\n该版本下的 ' + (v.nodes || []).length + ' 个时间段会一并删除, 不可恢复。')) return;
    try {
      await Api.deleteScheduleVersion(this.schoolId, v.effective_from);
      await this.load();
      if (this.onSaved) this.onSaved();
    } catch (e) {
      console.error('[school-schedule] delete version failed', e);
      this.error = this.extractError(e);
      m.redraw();
    }
  }

  // ── 渲染 ────────────────────────────────────────────────────

  className() {
    return 'Modal--large ZS-sched-modal';
  }
  title() {
    return m("div", null, m("div", {
      className: "ZS-sched-title"
    }, m("i", {
      className: "fas fa-clock",
      style: "color:var(--zs-warning-f59e0b, #f59e0b);margin-right:8px;"
    }), "\u8BFE\u8868"), m("div", {
      className: "ZS-sched-subtitle"
    }, this.school && this.school.name, " \xB7 \u8FD9\u4E00\u4EFD\u51B3\u5B9A\u8BE5\u6821\u533A\u6BCF\u5929\u6709\u54EA\u4E9B\u53EF\u7EA6\u65F6\u95F4\u6BB5"));
  }

  /**
   * v0.17.67 — 这一行结束 → 下一行开始之间那道边界的描述。
   *
   * ⚠️ 改前只有 `nm > endMin` 才渲染, 相邻两段首尾相接或**重叠**时 gapNode 保持 null,
   *   一个字都不渲染。辉哥生产那份课表 (21 段 / 全部 45 分钟 / 15 分钟错位双轨) 20 个
   *   相邻对里有 10 对是重叠, 这 10 对全被静默吞掉 —— 界面上两行看着像无缝衔接,
   *   实际是重叠。所以现在三种情况都显式渲染, 中性灰、同字号同字重, 只靠文字区分。
   *
   * ⚠️ 绝不用红色: 重叠是这份课表的刻意设计 (奇偶完美交替), 10/20 行亮红就是常态噪音,
   *   报警也就失去意义了。全仓只有删除确认弹窗的「删除」按钮该是红的。
   *
   * ⚠️ 不用 fmtDuration 之外的写法: 它是本组件既有的分钟格式化 (返回 '1h' / '45m'),
   *   沿用它才能保证桌面「间隔 1h」这类存量文案逐字不变 (判据 4 桌面零回归)。
   *
   * 顺序错乱 / 跨夜不用处理: 后端 ValidatesScheduleVersionPayload::assertNodesOrdered()
   * 强制 start_time 严格递增, 那种情况在产品里不可达。
   */
  gapLabel(node, i, endMin) {
    const next = this.form.nodes[i + 1];
    if (!next) return null;
    const nm = toMinutes(next.start_time);
    if (nm === null || endMin === null) return null;
    const d = nm - endMin;
    if (d > 0) return '间隔 ' + fmtDuration(d);
    if (d === 0) return '首尾相接';
    return '重叠 ' + fmtDuration(-d);
  }
  nodeDuration(node) {
    const s = toMinutes(node.start_time);
    const e = toMinutes(node.end_time);
    return s !== null && e !== null && e > s ? e - s : null;
  }
  nodeRow(node, i) {
    const err = this.rowError(node.key);
    const duration = this.nodeDuration(node);
    const gap = this.gapLabel(node, i, toMinutes(node.end_time));

    // ── 移动端: 行内只留 段号 / 开始 → 结束 / 删除, 时长下沉到下面的连接线 ──
    if (this.isMobileLayout) {
      return m("div", {
        className: 'ZS-sched-node ZS-sched-node--m' + (err ? ' bad' : ''),
        key: node.key
      }, m("span", {
        className: 'ZS-sched-ix' + (err ? ' bad' : '')
      }, i + 1), m("input", {
        className: "ZS-sched-time ZS-sched-time--native",
        type: "time",
        step: "300",
        value: toInputTime(node.start_time),
        onchange: e => this.onNativeTimeChange(node, 'start_time', e.target)
      }), m("span", {
        className: "ZS-sched-arrow"
      }, "\u2192"), m("input", {
        className: "ZS-sched-time ZS-sched-time--native",
        type: "time",
        step: "300",
        value: toInputTime(node.end_time),
        onchange: e => this.onNativeTimeChange(node, 'end_time', e.target)
      }), err && m("span", {
        className: "ZS-sched-err-ic",
        title: err
      }, m("i", {
        className: "fas fa-triangle-exclamation"
      })), this.canManage && m("button", {
        type: "button",
        className: "ZS-sched-del ZS-sched-del--m",
        title: '删除第 ' + (i + 1) + ' 段',
        "aria-label": '删除第 ' + (i + 1) + ' 段（' + (node.start_time || '') + ' 至 ' + (node.end_time || '') + '）',
        onclick: () => this.askRemoveNode(node, i)
      }, m("i", {
        className: "fas fa-trash"
      })));
    }

    // ── 桌面: 结构与 v0.17.66a 一模一样, 一个属性都没动 ──
    return m("div", {
      className: 'ZS-sched-node' + (err ? ' bad' : ''),
      key: node.key
    }, m("span", {
      className: "ZS-sched-handle",
      title: "\u62D6\u52A8\u6392\u5E8F"
    }, "\u22EE\u22EE"), m("span", {
      className: 'ZS-sched-ix' + (err ? ' bad' : '')
    }, i + 1), m("div", {
      className: "ZS-sched-fp"
    }, m("span", {
      className: "ZS-sched-fp-ic"
    }, m("i", {
      className: "far fa-clock"
    })), m("input", {
      className: "ZS-sched-time",
      type: "text",
      "data-fp": node.key + ':start_time',
      placeholder: "09:00"
    }), m("span", {
      className: "ZS-sched-fp-sep"
    }), m("input", {
      className: "ZS-sched-time",
      type: "text",
      "data-fp": node.key + ':end_time',
      placeholder: "12:00"
    })), m("span", {
      className: "ZS-sched-dur"
    }, duration === null ? '—' : duration + ' 分'), gap && m("span", {
      className: "ZS-sched-gap"
    }, gap), !this.form.nodes[i + 1] && m("span", {
      className: "ZS-sched-gap last"
    }, "\u6700\u540E\u4E00\u4E2A"), err && m("span", {
      className: "ZS-sched-err",
      title: err
    }, m("i", {
      className: "fas fa-triangle-exclamation"
    }), " ", err), this.canManage && m("span", {
      className: "ZS-sched-del",
      title: "\u5220\u9664\u8FD9\u4E2A\u65F6\u95F4\u6BB5",
      onclick: () => this.askRemoveNode(node, i)
    }, m("i", {
      className: "fas fa-trash"
    })));
  }

  /**
   * v0.17.67 — 行间连接线 (仅移动端)。
   *
   * 连接线正好落在第 i 行与第 i+1 行之间: 上面那行刚结束, 所以**时长属于上一行**,
   * 而「间隔 / 重叠 / 首尾相接」描述的是这道边界本身 —— 两件事各归其位。
   * 移动端从行里省出来的那条净空就用来放它, 行高因此回到能塞下 40px 触控区的 52px。
   */
  connectorRow(node, i) {
    const duration = this.nodeDuration(node);
    const gap = this.gapLabel(node, i, toMinutes(node.end_time));
    const isLast = !this.form.nodes[i + 1];
    return m("div", {
      className: "ZS-sched-conn",
      key: 'conn-' + node.key
    }, m("span", {
      className: "ZS-sched-conn-stem"
    }), isLast ? m("span", {
      className: "ZS-sched-conn-lb tail"
    }, "\u672B\u6BB5") : m("span", {
      className: "ZS-sched-conn-lb"
    }, duration === null ? '— 分' : duration + ' 分', m("span", {
      className: "sep"
    }, "\xB7"), gap || '—'));
  }

  /** v0.17.67 — 移动端列表是 [行, 连接线, 行, 连接线, ...] 交错; 桌面仍是一行一项 */
  renderNodesList() {
    const nodes = this.form.nodes;
    const out = [];
    nodes.forEach((n, i) => {
      out.push(this.nodeRow(n, i));
      if (this.isMobileLayout) out.push(this.connectorRow(n, i));
    });
    return out;
  }

  /**
   * v0.17.67 — 删除二次确认框 (跨端)。
   *
   * 形状照抄 StudentContractModal.confirmView(); DOM 挂在 `.Modal-body` 末尾。
   * `.Modal-body` 自身是 static, 但 `.Modal-content` 是 relative, 所以 veil 的
   * `position:absolute; inset:0` 自然锚在 Modal-content 上盖住整个弹窗内容区。
   */
  renderDeleteConfirm() {
    const p = this.pendingDel;
    if (!p) return null;
    return m("div", {
      className: "ZS-sched-veil",
      role: "dialog",
      "aria-modal": "true"
    }, m("div", {
      className: "ZS-sched-cf"
    }, m("div", {
      className: "ZS-sched-cf-t"
    }, m("i", {
      className: "fas fa-trash-can",
      "aria-hidden": "true"
    }), m("span", null, "\u5220\u9664\u7B2C ", p.no, " \u6BB5\uFF1F")), m("div", {
      className: "ZS-sched-cf-row"
    }, m("span", {
      className: "n"
    }, "\u7B2C ", p.no, " \u6BB5"), m("span", {
      className: "t"
    }, p.start), m("span", {
      className: "d"
    }, "\u2013"), m("span", {
      className: "t"
    }, p.end)), m("p", {
      className: "ZS-sched-cf-note"
    }, "\u5220\u9664\u540E\u8BE5\u65F6\u6BB5\u5C06\u4E0D\u53EF\u518D\u7EA6\u8BFE\u3002"), m("div", {
      className: "ZS-sched-cf-acts"
    }, m("button", {
      type: "button",
      className: "cancel",
      onclick: () => this.cancelRemoveNode()
    }, "\u53D6\u6D88"), m("button", {
      type: "button",
      className: "ok",
      onclick: () => this.removeNode(p.key)
    }, m("i", {
      className: "fas fa-trash-can",
      "aria-hidden": "true"
    }), "\u5220\u9664"))));
  }

  /**
   * v0.18.98 — 「版本」下拉 = **Flarum 原生 `Dropdown`** (照抄 ConvertStudentModal.js 逐字同款)。
   *
   * ## 三个必须遵守的 core 契约 (踩过坑)
   *   ① 每个 child 必须对应一个 `<li>` —— core CSS 是**直接子选择器** `.Dropdown-menu > li > button`。
   *   ② `Dropdown` **没有「当前选中值」概念** —— `label` 是纯 `attrs.label`,
   *      选中态靠 `<Button active>` ⇒ 必须自己算 `current` 和 `label`。
   *   ③ `lazyDraw={true}` **必带** —— 否则 `shown.bs.dropdown` 的异步 `m.redraw()` 会抹掉 `.show`。
   *
   * ## ⛔ 与改前 `<select>` 的两处**语义**差别 (照改前的口径, 别自己发挥)
   *   ① 改前那个 `— 新建版本 —` 的 `<option>` **点了什么也不会发生**
   *      (`this.versions.find(x => x.effective_from === '')` = undefined ⇒ `if (v)` 不成立),
   *      它**只是个视觉占位** —— 真正的「新建版本」是右侧那个 `.ZS-sched-vbtn` 按钮。
   *      ⇒ 改成 Dropdown 后: **label** 在 `!selected` 时 = `— 新建版本 —`,
   *        但**选项里不放这一项** (放进去点了没反应 = 假按钮)。
   *   ② `key` 用 `v.effective_from` (字符串) —— 照抄, 别改成 id。
   *
   * ## ⛔ 按钮外观
   *   `.ZS-sched-pick` 是给**原生 `<select>`** 写的 (含 appearance / 高度 / 内边距),
   *   套到 Flarum 的 `.Button` 上会变形 ⇒ ⛔ **不**用它当 `buttonClassName`,
   *   本批统一用 `buttonClassName="ZS-form-input"` (与另外两处一致)。
   */
  renderVersionDropdown() {
    const selected = this.editingVid;
    const current = this.versions.find(v => v.effective_from === selected) || null;
    const labelOf = v => this.versionLabel(v) + (v.is_current ? ' (当前)' : '');
    const items = this.versions.map(v => m(Button, {
      key: v.effective_from,
      active: !!current && current.effective_from === v.effective_from,
      onclick: () => this.selectVersion(v)
    }, labelOf(v)));
    return m(Dropdown, {
      className: "ZSC-choice-dropdown",
      buttonClassName: "ZS-form-input",
      menuClassName: "ZSC-choice-dropdown-menu",
      label: current ? labelOf(current) : '— 新建版本 —',
      lazyDraw: true
    }, items);
  }
  renderVersionBar() {
    return m("div", {
      className: "ZS-sched-verbar"
    }, m("span", {
      className: "lb"
    }, "\u7248\u672C"), this.versions.length ? this.renderVersionDropdown() : m("span", {
      className: "ZS-sched-pick empty"
    }, "\u8FD8\u6CA1\u6709\u4EFB\u4F55\u7248\u672C"), this.canManage && m("span", {
      className: "rt"
    }, m("button", {
      className: "ZS-sched-vbtn",
      onclick: () => this.startNew()
    }, m("i", {
      className: "fas fa-plus"
    }), " \u65B0\u5EFA\u7248\u672C"), m("button", {
      className: "ZS-sched-vbtn",
      disabled: !this.versions.length,
      onclick: () => {
        const cur = this.versions.find(x => x.effective_from === this.editingVid) || this.currentVersion() || this.versions[0];
        if (cur) this.copyAsNew(cur);
      }
    }, m("i", {
      className: "fas fa-copy"
    }), " \u590D\u5236\u4E3A\u65B0\u7248\u672C")));
  }
  renderNodes() {
    const nodes = this.form.nodes;
    if (!nodes.length) {
      return m("div", {
        className: "ZS-sched-empty"
      }, m("i", {
        className: "far fa-clock",
        style: "font-size:28px;color:var(--zs-text-3-5e6673, #5e6673);"
      }), m("div", {
        className: "t"
      }, "\u8FD8\u6CA1\u914D\u7F6E, \u53BB\u914D\u7F6E"), m("div", {
        className: "s"
      }, "\u914D\u7F6E\u5B8C\u8FD9\u4E00\u4EFD, \u8BE5\u6821\u533A\u6BCF\u5929\u5C31\u6709\u53EF\u7EA6\u65F6\u95F4\u6BB5\u4E86 (\u8BFE\u8868\u65E5\u5386\u6309\u5B83\u6E32\u67D3)"), this.canManage && m("button", {
        className: "ZS-sched-add",
        onclick: () => this.addNode()
      }, m("i", {
        className: "fas fa-plus"
      }), " \u6DFB\u52A0\u65F6\u95F4\u6BB5"));
    }
    return m("div", null, m("div", {
      className: "ZS-sched-nodes"
    }, this.renderNodesList()), this.canManage && m("button", {
      className: "ZS-sched-add",
      onclick: () => this.addNode()
    }, m("i", {
      className: "fas fa-plus"
    }), " \u6DFB\u52A0\u65F6\u95F4\u6BB5"));
  }
  renderHistory() {
    if (!this.versions.length) return null;
    const current = this.currentVersion();
    return m("div", {
      className: "ZS-sched-hist"
    }, m("div", {
      className: "ZS-sched-hist-hd"
    }, m("i", {
      className: "far fa-clock",
      style: "margin-right:6px;"
    }), "\u5386\u53F2\u7248\u672C", m("span", {
      className: "ZS-sched-hist-count"
    }, "\u5171 ", this.versions.length, " \u4E2A")), this.versions.map(v => m("div", {
      className: 'ZS-sched-hist-item' + (v.effective_from === this.editingVid ? ' active' : ''),
      key: v.effective_from
    }, m("span", {
      className: "vr"
    }, v.version_name || '(未命名)'), m("span", {
      className: "rg"
    }, v.effective_from, " \u2192 ", v.effective_to || '至今', v.is_current && m("span", {
      className: "cv"
    }, "\u5F53\u524D")), m("span", {
      className: "rc"
    }, v.nodes_count != null ? v.nodes_count : (v.nodes || []).length, " \u4E2A\u65F6\u95F4\u6BB5"), m("span", {
      className: "bb"
    }, v.is_current ? m("button", {
      className: "ZS-sched-link",
      onclick: () => this.selectVersion(v)
    }, "\u7F16\u8F91") : m("button", {
      className: "ZS-sched-link",
      onclick: () => this.selectVersion(v)
    }, "\u67E5\u770B"), this.canManage && m("button", {
      className: "ZS-sched-link",
      onclick: () => this.copyAsNew(v)
    }, "\u590D\u5236\u4E3A\u65B0\u7248\u672C"), this.canManage && (v.is_current ? m("button", {
      className: "ZS-sched-link del",
      disabled: true,
      title: "\u5F53\u524D\u751F\u6548\u7248\u672C\u4E0D\u53EF\u5220\u9664, \u8BF7\u65B0\u5EFA\u7248\u672C\u540E\u518D\u5220\u5386\u53F2\u7248\u672C"
    }, "\u5220\u9664") : m("button", {
      className: "ZS-sched-link del",
      onclick: () => this.deleteVersion(v)
    }, "\u5220\u9664"))))));
  }
  content() {
    if (this.loading) {
      return m("div", {
        className: "Modal-body"
      }, m("div", {
        className: "ZS-sched-loading"
      }, "\u52A0\u8F7D\u4E2D..."));
    }
    const v = this.validate();
    this._rowErrors = v.errors;
    // v0.17.83 —— 「保存」按钮的置灰判据就是这一行的 validate(), 所以脏历史版本的拦截在
    //   用户点保存**之前**就生效 (submit() 不会跑, 它设的 this.error 也就不会出现)。
    //   这里只把「数据本身坏了」这一类 (dataFault) 提前显示出来 —— 其余 global 校验
    //   (版本名空 / 日期格式错 …) 的渲染**一字未动**, 不顺手扩大改动面。
    this._dataFault = v.dataFault ? v.global : null;
    const blocked = !v.ok;
    return m("div", null, m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "ZS-sched-form"
    }, this.renderVersionBar(), this.listWarning && m("div", {
      className: "ZS-sched-warn"
    }, m("i", {
      className: "fas fa-triangle-exclamation"
    }), " ", this.listWarning), m("div", {
      className: "ZS-sched-head-fields"
    }, m("div", {
      className: "ZS-sched-field"
    }, m("label", null, "\u7248\u672C\u540D ", m("span", {
      className: "required"
    }, "*")), m("input", {
      className: "ZS-form-input",
      placeholder: "\u4F8B\u5982: 2026\u79CB",
      disabled: !this.canManage,
      value: this.form.version_name,
      oninput: e => {
        this.form.version_name = e.target.value;
        m.redraw();
      }
    }))), m("div", {
      className: "ZS-sched-section-title"
    }, m("i", {
      className: "fas fa-list-ol"
    }), "\u65F6\u95F4\u6BB5", this.isMobileLayout ? m("span", {
      className: "ZS-sched-hint"
    }, "\u4ECE\u4E0A\u5230\u4E0B\u5373\u8BFE\u8868\u987A\u5E8F \xB7 \u9700\u8981\u6539\u987A\u5E8F\u8BF7\u5728\u7535\u8111\u4E0A\u8C03\u6574") : m("span", {
      className: "ZS-sched-hint"
    }, "\u53EF\u62D6\u62FD\u6392\u5E8F \xB7 \u6570\u7EC4\u987A\u5E8F = \u8BFE\u8868\u4ECE\u4E0A\u5230\u4E0B\u7684\u663E\u793A\u987A\u5E8F")), this.renderNodes(), this.renderHistory(), (this.error || this._dataFault) && m("div", {
      className: "ZS-sched-error"
    }, m("i", {
      className: "fas fa-triangle-exclamation"
    }), " ", this.error || this._dataFault)), this.renderDeleteConfirm()), m("div", {
      className: "Modal-footer"
    }, m("div", {
      className: "footer-warn"
    }, this.editingVid ? m('[', null, m("i", {
      className: "fas fa-pen-to-square",
      style: "margin-right:4px;"
    }), "\u7F16\u8F91\u7248\u672C ", this.form.version_name, " (", this.editingVid, ") \xB7 \u4FDD\u5B58\u65F6\u65F6\u95F4\u6BB5\u5168\u91CF\u66FF\u6362") : m('[', null, m("i", {
      className: "fas fa-plus-circle",
      style: "margin-right:4px;"
    }), "\u65B0\u5EFA\u8BFE\u8868\u7248\u672C (\u65E7\u7248\u672C\u539F\u6837\u4FDD\u7559)")), m("div", {
      className: "footer-actions"
    }, m("button", {
      className: "Button",
      onclick: () => this.hide()
    }, "\u53D6\u6D88"), m("button", {
      className: "Button Button--primary",
      disabled: this.saving || blocked || !this.canManage,
      onclick: () => this.submit()
    }, this.saving ? '保存中...' : m("span", null, m("i", {
      className: "fas fa-check",
      style: "margin-right:4px;"
    }), "\u4FDD\u5B58")))));
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    // Modal 首帧渲染完才有 this.element, 此时节点 input 才在 DOM 里
    this.syncPickers();
    this.rebuildSortable();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    // 每次 redraw 后重新对齐 flatpickr 与拖拽 (删除/新增/重排都会换 DOM)
    this.syncPickers();
    this.rebuildSortable();
    // v0.17.71 — 确认框刚挂上来时兜底滚进视口 (常态下是空操作)
    this.ensureConfirmVisible();
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/SchoolScheduleModal', SchoolScheduleModal);